import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import './AgentCallsSkeleton.css';

/** Card rows drawn in the list pane — enough to fill a laptop-height list. */
const CARD_ROWS = [0, 1, 2, 3];

/** Toolbar filters, by the min-widths of the real ones (Assignee, Clusters, Channel). */
const FILTER_WIDTHS = [104, 96, 92];

const Bone: React.FC<{ className: string; style?: React.CSSProperties }> = ({
  className,
  style,
}) => <span className={`agent-calls-skeleton__bone ${className}`} style={style} />;

const CardSkeleton: React.FC = () => (
  <div className="agent-calls-skeleton__card">
    <div className="agent-calls-skeleton__card-head">
      <Bone className="agent-calls-skeleton__chan" />
      <Bone className="agent-calls-skeleton__line agent-calls-skeleton__line--name" />
      <Bone className="agent-calls-skeleton__line agent-calls-skeleton__line--meta" />
    </div>
    <Bone className="agent-calls-skeleton__line" />
    <Bone className="agent-calls-skeleton__line agent-calls-skeleton__line--short" />
    <Bone className="agent-calls-skeleton__line agent-calls-skeleton__line--date" />
    <Bone className="agent-calls-skeleton__pill" />
  </div>
);

/**
 * The list + detail panes as the page first draws them: the list of call cards
 * beside the "select a call" pane (no call is selected on arrival, so the
 * details panel is not there yet). On a phone only the list shows, as on the page.
 */
const BodySkeleton: React.FC = () => (
  <div className="agent-calls-skeleton__layout" aria-hidden="true">
    <div className="agent-calls-skeleton__list">
      <Bone className="agent-calls-skeleton__line agent-calls-skeleton__line--count" />
      {CARD_ROWS.map((row) => (
        <CardSkeleton key={row} />
      ))}
    </div>
    <div className="agent-calls-skeleton__detail">
      <Bone className="agent-calls-skeleton__hint-icon" />
      <Bone className="agent-calls-skeleton__line agent-calls-skeleton__line--hint" />
    </div>
  </div>
);

export interface AgentCallsSkeletonProps {
  /**
   * `page` (default): the whole screen — breadcrumb, toolbar and panes — for the
   * route's Suspense fallback and the agent layout's loading state.
   * `body`: only the panes, for the page itself while the first page of calls
   * loads; its real header stays mounted (and keeps the search box's focus).
   */
  variant?: 'page' | 'body';
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of Inbox → Agent Calls (SL-1908). One shape from a cold load
 * to data: `RouteSkeleton` (inside the app chrome), the agent layout while the
 * agents load, the route's Suspense fallback and the page while its first page
 * of calls loads all draw this.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. No visible text; its accessible name is translated.
 */
export const AgentCallsSkeleton: React.FC<AgentCallsSkeletonProps> = ({
  variant = 'page',
  standalone = false,
}) => {
  const { t } = useTranslation(NS);
  const modifiers = [
    `agent-calls-skeleton--${variant}`,
    standalone ? 'agent-calls-skeleton--standalone' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`agent-calls-skeleton ${modifiers}`}
      role="status"
      aria-busy="true"
      aria-label={t('loading.aria')}
      data-testid={`agent-calls-skeleton-${variant}`}
    >
      {variant === 'page' && (
        <div className="agent-calls-skeleton__header" aria-hidden="true">
          <Bone className="agent-calls-skeleton__crumb" />
          <div className="agent-calls-skeleton__toolbar">
            <Bone className="agent-calls-skeleton__tabs" />
            <Bone className="agent-calls-skeleton__search" />
            <div className="agent-calls-skeleton__filters">
              <Bone className="agent-calls-skeleton__filters-label" />
              {FILTER_WIDTHS.map((width) => (
                <Bone key={width} className="agent-calls-skeleton__select" style={{ width }} />
              ))}
            </div>
          </div>
        </div>
      )}
      <BodySkeleton />
    </div>
  );
};
